// Mercapoli - Restaurant Detail Page
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Star, Clock, MapPin, Heart, Plus, Minus, ArrowLeft, 
  ShoppingCart, ChevronRight, Loader2, Phone
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea, ScrollBar } from '../../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { restaurantes as restaurantesApi, favoritos as favoritosApi } from '../../lib/api';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';

const RestaurantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, forceAddItem, setRestaurante, itemCount, restauranteId } = useCart();

  const [loading, setLoading] = useState(true);
  const [restaurante, setRestauranteState] = useState(null);
  const [menu, setMenu] = useState([]);
  const [isFavorito, setIsFavorito] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Modal producto
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    loadRestaurante();
  }, [id]);

  const loadRestaurante = async () => {
    setLoading(true);
    try {
      const restData = await restaurantesApi.getById(id);
      setRestauranteState(restData);
      
      const menuData = await restaurantesApi.getMenu(id);
      setMenu(menuData);
      
      // Obtener categorías únicas del menú
      const cats = [...new Set(menuData.map(p => p.categoria))];
      if (cats.length > 0 && !selectedCategory) {
        setSelectedCategory(cats[0]);
      }
    } catch (error) {
      toast.error('Error al cargar el restaurante');
      navigate('/app');
    }
    setLoading(false);
  };

  const toggleFavorito = async () => {
    try {
      if (isFavorito) {
        await favoritosApi.remove(id);
        setIsFavorito(false);
        toast.success('Eliminado de favoritos');
      } else {
        await favoritosApi.add(id);
        setIsFavorito(true);
        toast.success('Agregado a favoritos');
      }
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    }
  };

  const handleAddToCart = () => {
    if (!selectedProduct) return;

    const result = addItem(selectedProduct, quantity, notes);
    
    if (result.needsConfirmation) {
      setShowConfirmDialog(true);
      return;
    }

    setRestaurante(restaurante);
    toast.success(`${selectedProduct.nombre} agregado al carrito`);
    closeProductModal();
  };

  const handleForceAdd = () => {
    forceAddItem(selectedProduct, quantity, notes, restaurante);
    toast.success(`${selectedProduct.nombre} agregado al carrito`);
    setShowConfirmDialog(false);
    closeProductModal();
  };

  const openProductModal = (product) => {
    setSelectedProduct(product);
    setQuantity(1);
    setNotes('');
  };

  const closeProductModal = () => {
    setSelectedProduct(null);
    setQuantity(1);
    setNotes('');
  };

  // Obtener categorías del menú
  const categories = [...new Set(menu.map(p => p.categoria))];
  const filteredMenu = selectedCategory 
    ? menu.filter(p => p.categoria === selectedCategory)
    : menu;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-spicy-coral" />
      </div>
    );
  }

  if (!restaurante) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Hero Image */}
      <div className="relative h-64 sm:h-80">
        <img
          src={restaurante.imagen}
          alt={restaurante.nombre}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 p-2 rounded-full bg-white/90 hover:bg-white transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>

        {/* Favorite button */}
        <button
          onClick={toggleFavorito}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/90 hover:bg-white transition-colors"
        >
          <Heart className={`h-5 w-5 ${isFavorito ? 'fill-spicy-coral text-spicy-coral' : ''}`} />
        </button>

        {/* Restaurant info overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <div className="container-app">
            <div className="flex items-start gap-4">
              {restaurante.logo && (
                <img 
                  src={restaurante.logo} 
                  alt={restaurante.nombre}
                  className="w-16 h-16 rounded-xl object-cover border-2 border-white"
                />
              )}
              <div className="flex-1">
                <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1">
                  {restaurante.nombre}
                </h1>
                <p className="text-white/80 text-sm line-clamp-2 mb-2">
                  {restaurante.descripcion}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-medium">{restaurante.calificacion}</span>
                    <span className="text-white/70">({restaurante.total_resenas} reseñas)</span>
                  </div>
                  <span className="text-white/50">•</span>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{restaurante.tiempo_preparacion_min}-{restaurante.tiempo_preparacion_min + 15} min</span>
                  </div>
                  <span className="text-white/50">•</span>
                  <span>${restaurante.costo_envio.toFixed(0)} envío</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info cards */}
      <div className="container-app py-4">
        <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
          <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{restaurante.direccion}</span>
          </div>
          <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{restaurante.horario_apertura} - {restaurante.horario_cierre}</span>
          </div>
          <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{restaurante.telefono}</span>
          </div>
        </div>
      </div>

      {/* Category tabs */}
      <div className="sticky top-0 z-40 bg-background border-b border-border">
        <div className="container-app">
          <ScrollArea className="w-full">
            <div className="flex gap-2 py-3">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    selectedCategory === cat
                      ? 'bg-spicy-coral text-white'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
      </div>

      {/* Menu */}
      <div className="container-app py-6">
        <h2 className="font-heading text-xl font-bold mb-4">
          {selectedCategory || 'Menú'}
        </h2>
        
        <div className="grid gap-4 sm:grid-cols-2">
          {filteredMenu.map((producto, index) => (
            <motion.button
              key={producto.producto_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => openProductModal(producto)}
              className="flex gap-4 p-4 bg-card rounded-xl border border-border hover:border-spicy-coral/50 transition-colors text-left"
              data-testid={`product-${producto.producto_id}`}
            >
              <div className="flex-1">
                <h3 className="font-medium mb-1">{producto.nombre}</h3>
                <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                  {producto.descripcion}
                </p>
                <div className="flex items-center gap-2">
                  <span className="font-heading text-lg font-bold text-spicy-coral">
                    ${producto.precio.toFixed(2)}
                  </span>
                  {!producto.disponible && (
                    <Badge variant="secondary">Agotado</Badge>
                  )}
                </div>
              </div>
              {producto.imagen && (
                <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                  <img
                    src={producto.imagen}
                    alt={producto.nombre}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Cart button - floating */}
      {itemCount > 0 && restauranteId === id && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background via-background to-transparent"
        >
          <Button
            onClick={() => navigate('/app/carrito')}
            className="w-full bg-spicy-coral hover:bg-spicy-coral/90 py-6 rounded-full shadow-glow"
            data-testid="view-cart-btn"
          >
            <ShoppingCart className="mr-2 h-5 w-5" />
            Ver carrito ({itemCount} items)
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </motion.div>
      )}

      {/* Product modal */}
      <Dialog open={!!selectedProduct} onOpenChange={() => closeProductModal()}>
        <DialogContent className="sm:max-w-md">
          {selectedProduct && (
            <>
              {selectedProduct.imagen && (
                <div className="relative h-48 -mx-6 -mt-6 mb-4 rounded-t-lg overflow-hidden">
                  <img
                    src={selectedProduct.imagen}
                    alt={selectedProduct.nombre}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">
                  {selectedProduct.nombre}
                </DialogTitle>
              </DialogHeader>
              
              <p className="text-muted-foreground text-sm mb-4">
                {selectedProduct.descripcion}
              </p>

              <div className="mb-4">
                <label className="text-sm font-medium mb-2 block">
                  Notas especiales (opcional)
                </label>
                <Textarea
                  placeholder="Ej: Sin cebolla, extra picante..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="resize-none"
                  rows={2}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-2 rounded-full bg-muted hover:bg-muted/80"
                    disabled={quantity <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="font-heading text-xl font-bold w-8 text-center">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-2 rounded-full bg-muted hover:bg-muted/80"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                
                <Button
                  onClick={handleAddToCart}
                  className="bg-spicy-coral hover:bg-spicy-coral/90"
                  disabled={!selectedProduct.disponible}
                  data-testid="add-to-cart-btn"
                >
                  Agregar ${(selectedProduct.precio * quantity).toFixed(2)}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm dialog - different restaurant */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¿Cambiar de restaurante?</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Ya tienes productos de otro restaurante en tu carrito. 
            ¿Deseas vaciar el carrito y agregar este producto?
          </p>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleForceAdd}
              className="flex-1 bg-spicy-coral hover:bg-spicy-coral/90"
            >
              Sí, cambiar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RestaurantDetail;
